import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { useServices } from 'hooks/useServices';
import { useRefundForm } from 'hooks/useRefundForm';
import { RefundFieldset } from 'containers/RefundFieldset';
import { useAsyncBoundary } from 'utils/asyncBoundary/asyncBoundaryHooks';
import { M8BillRow } from 'services/production/types';
import { validateRefundFormValues } from 'utils/refundUtils';
import { RefundAmountExceedsTheBalance } from 'utils/errors';
import { SidebarModalSectionComponent } from 'design/sidebarModal';
import { TextComponent } from 'design/text/textComponents';
import { FormControlComponent } from 'design/formControl';
import { MarginComponent } from 'design/margin';
import { ModalPressControlComponent } from 'design/modalPressControl';
import { CustomFieldsetComponent } from 'design/customFieldset/customFieldsetComponents';

type Props = {
  clientId: number;
  refundId: null | number;
  m8BillRows: M8BillRow[];
  onCancel(): void;
  onSubmitDidSucceed(): void;
};

export const ClientDetailsRefundEditor = (props: Props) => {
  const {
    refundId,
    clientId,
    m8BillRows,
    onCancel,
    onSubmitDidSucceed,
  } = props;

  const intl = useIntlDictionary();

  const services = useServices();

  const { active, injectPromise } = useAsyncBoundary();

  const {
    values,
    violations,
    setValues,
    setViolations,
    setAmount,
    setComment,
  } = useRefundForm();

  React.useLayoutEffect(() => {
    const row = m8BillRows.find((row) => row.id === refundId);

    if (row) {
      setValues({
        amount: `${row.amount}`,
        comment: row.comment || '',
      });
    }
  }, [m8BillRows, refundId, setValues]);

  React.useLayoutEffect(() => {
    if (refundId === null) {
      setValues((prev) => ({
        ...prev,
        comment: intl.clientDetails.refundEditor.defaultCommentText,
      }));
    }
  }, [intl.clientDetails.refundEditor.defaultCommentText, refundId, setValues]);

  const handleSubmitError = React.useCallback(
    (error) => {
      if (error instanceof RefundAmountExceedsTheBalance) {
        setViolations([
          {
            name: 'AmountExceedsBalance',
          },
        ]);
      } else {
        throw error;
      }
    },
    [setViolations]
  );

  const submit = React.useCallback(() => {
    const nextViolations = validateRefundFormValues(values);

    if (nextViolations.length === 0) {
      const promise = services
        .submitRefundForm({
          refundId,
          clientId,
          values,
        })
        .then(onSubmitDidSucceed)
        .catch(handleSubmitError);

      injectPromise(promise, { shouldShowFallback: false });
    } else {
      Promise.resolve(nextViolations).then(setViolations);
    }
  }, [
    clientId,
    handleSubmitError,
    injectPromise,
    onSubmitDidSucceed,
    refundId,
    services,
    setViolations,
    values,
  ]);

  return (
    <>
      <SidebarModalSectionComponent
        theme="head"
        content={
          <TextComponent size="mpp" weight="medium">
            {intl.clientDetails.refundEditor.titleText}
          </TextComponent>
        }
        actions={null}
        navigation={null}
      />
      <FormControlComponent onSubmit={submit}>
        <SidebarModalSectionComponent
          content={
            <CustomFieldsetComponent>
              <RefundFieldset
                disabled={active}
                values={values}
                violations={violations}
                onAmountChange={setAmount}
                onCommentChange={setComment}
              />
            </CustomFieldsetComponent>
          }
          actions={
            <>
              <ModalPressControlComponent theme="primary" type="submit">
                {intl.clientDetails.refundEditor.submitButtonText}
              </ModalPressControlComponent>
              <MarginComponent top={20} />
              <ModalPressControlComponent
                theme="secondary"
                type="button"
                onPress={onCancel}
              >
                {intl.cancelButton}
              </ModalPressControlComponent>
            </>
          }
          navigation={null}
        />
      </FormControlComponent>
    </>
  );
};
